import { supabase } from '../lib/supabase';
import { StatType, ScoresheetLog, UIMatchStats, UIScoresheetStat } from '../types/database';

// Cache for stat_type_id mappings to improve performance
const statTypeCache = new Map<string, string>();

export const scoresheetService = {
  /**
   * Initialize the stat type cache for faster lookups
   */
  async initializeStatTypeCache(): Promise<void> {
    // Initialize stat type cache
    
    const { data: statTypes, error } = await supabase
      .from('stat_types')
      .select('id, stat_key')
      .eq('is_active', true);

    if (error) {
      console.error('Error loading stat types:', error);
      throw error;
    }

    // Clear existing cache and populate with new data
    statTypeCache.clear();
    statTypes?.forEach(stat => {
      statTypeCache.set(stat.stat_key, stat.id);
    });

    // Cache loaded successfully
  },

  /**
   * Get stat type ID from cache or database
   */
  async getStatTypeId(statKey: string): Promise<string> {
    // Check cache first
    if (statTypeCache.has(statKey)) {
      return statTypeCache.get(statKey)!;
    }

    // If not in cache, fetch from database
    const { data, error } = await supabase
      .from('stat_types')
      .select('id')
      .eq('stat_key', statKey)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error(`Error fetching stat type for key: ${statKey}`, error);
      // Log available stat types for debugging
      throw new Error(`Stat type '${statKey}' not found in database`);
    }

    // Cache the result for future use
    statTypeCache.set(statKey, data.id);
    return data.id;
  },

  /**
   * Get all stat types (for debugging)
   */
  async getAllStatTypes(): Promise<any[]> {
    const { data, error } = await supabase
      .from('stat_types')
      .select('*')
      .eq('is_active', true);
    
    if (error) {
      console.error('Error fetching all stat types:', error);
      return [];
    }
    
    return data || [];
  },

  /**
   * Ensure substitution stat exists in database
   */
  async ensureSubstitutionStatExists(): Promise<void> {
    try {
      // Check if substitution stat already exists
      const { data: existingStat, error: checkError } = await supabase
        .from('stat_types')
        .select('id')
        .eq('stat_key', 'substitution')
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error checking for substitution stat:', checkError);
        return;
      }

      if (!existingStat) {
        // Create substitution stat type
        // Create the substitution stat type
        const { error: insertError } = await supabase
          .from('stat_types')
          .insert({
            category: 'game_flow',
            stat_key: 'substitution',
            display_name: 'Substitution',
            short_description: 'Player substitution',
            event_result: 'neutral',
            is_active: true,
            sort_order: 3
          });

        if (insertError) {
          console.error('Error creating substitution stat:', insertError);
          throw insertError;
        }

        // Substitution stat type created successfully
        // Clear cache to force refresh
        statTypeCache.clear();
      }
    } catch (error) {
      console.error('Error ensuring substitution stat exists:', error);
      throw error;
    }
  },

  /**
   * Get all stat types (for UI rendering)
   */
  async getStatTypes(): Promise<StatType[]> {
    // Fetch all stat types
    
    const { data, error } = await supabase
      .from('stat_types')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (error) {
      console.error('Error fetching stat types:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Get stats for a specific team in a match - for reports (only visible stats)
   * Optimized to fetch only the requested team's stats server-side
   */
  async getTeamStatsForReports(matchId: string, teamId: string): Promise<UIScoresheetStat[]> {
    // Fetch only this team's stats with stat type details
    const { data: scoresheetData, error } = await supabase
      .from('scoresheet')
      .select(`
        *,
        stat_type:stat_types(*),
        team:teams(id, name)
      `)
      .eq('match_id', matchId)
      .eq('team_id', teamId);

    if (error) {
      console.error('Error fetching team stats for reports:', error);
      throw error;
    }

    // Filter to only include stats marked as visible in reports
    const filteredData = scoresheetData?.filter(entry => 
      entry.stat_type && entry.stat_type.visible_in_reports === true
    ) || [];

    // Transform to UIScoresheetStat format
    return filteredData.map(entry => ({
      stat_type: entry.stat_type,
      value: entry.value,
      team_id: entry.team_id
    }));
  },

  /**
   * Get stats for a specific team in a match - for score app (all stats)
   * Optimized to fetch only the requested team's stats server-side
   */
  async getTeamStats(matchId: string, teamId: string): Promise<UIScoresheetStat[]> {
    // Fetch only this team's stats with stat type details
    const { data: scoresheetData, error } = await supabase
      .from('scoresheet')
      .select(`
        *,
        stat_type:stat_types(*),
        team:teams(id, name)
      `)
      .eq('match_id', matchId)
      .eq('team_id', teamId);

    if (error) {
      console.error('Error fetching team stats:', error);
      throw error;
    }

    // Transform to UIScoresheetStat format
    return (scoresheetData || []).map(entry => ({
      stat_type: entry.stat_type,
      value: entry.value,
      team_id: entry.team_id
    }));
  },

  /**
   * Get all stats for a match (both teams) - for reports (only visible stats)
   */
  async getMatchStatsForReports(matchId: string): Promise<UIMatchStats> {
    // Fetch match stats for reports
    
    // Get all scoresheet entries for this match with stat type details
    const { data: scoresheetData, error } = await supabase
      .from('scoresheet')
      .select(`
        *,
        stat_type:stat_types(*),
        team:teams(id, name)
      `)
      .eq('match_id', matchId);

    if (error) {
      console.error('Error fetching match stats for reports:', error);
      throw error;
    }

    // Filter to only include stats marked as visible in reports
    const filteredData = scoresheetData?.filter(entry => 
      entry.stat_type && entry.stat_type.visible_in_reports === true
    ) || [];

    // Filter visible stats for reports

    // Get match details to identify home and away teams
    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .select('home_team_id, away_team_id')
      .eq('id', matchId)
      .single();

    if (matchError) {
      console.error('Error fetching match details:', matchError);
      throw matchError;
    }

    const homeTeamId = matchData.home_team_id;
    const awayTeamId = matchData.away_team_id;

    // Separate stats by team
    const homeTeamStats: UIScoresheetStat[] = [];
    const awayTeamStats: UIScoresheetStat[] = [];

    filteredData.forEach(entry => {
      const uiStat: UIScoresheetStat = {
        stat_type: entry.stat_type,
        value: entry.value,
        team_id: entry.team_id
      };

      if (entry.team_id === homeTeamId) {
        homeTeamStats.push(uiStat);
      } else if (entry.team_id === awayTeamId) {
        awayTeamStats.push(uiStat);
      }
    });

    return {
      match_id: matchId,
      home_team_stats: homeTeamStats,
      away_team_stats: awayTeamStats
    };
  },

  /**
   * Get all stats for a match (both teams) - for score app (all stats)
   */
  async getMatchStats(matchId: string): Promise<UIMatchStats> {
    // Fetch match stats
    
    // Get all scoresheet entries for this match with stat type details
    const { data: scoresheetData, error } = await supabase
      .from('scoresheet')
      .select(`
        *,
        stat_type:stat_types(*),
        team:teams(id, name)
      `)
      .eq('match_id', matchId);

    if (error) {
      console.error('Error fetching match stats:', error);
      throw error;
    }

    // Get match details to identify home and away teams
    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .select('home_team_id, away_team_id')
      .eq('id', matchId)
      .single();

    if (matchError) {
      console.error('Error fetching match details:', matchError);
      throw matchError;
    }

    const homeTeamId = matchData.home_team_id;
    const awayTeamId = matchData.away_team_id;

    // Separate stats by team
    const homeTeamStats: UIScoresheetStat[] = [];
    const awayTeamStats: UIScoresheetStat[] = [];

    scoresheetData?.forEach(entry => {
      const uiStat: UIScoresheetStat = {
        stat_type: entry.stat_type,
        value: entry.value,
        team_id: entry.team_id
      };

      if (entry.team_id === homeTeamId) {
        homeTeamStats.push(uiStat);
      } else if (entry.team_id === awayTeamId) {
        awayTeamStats.push(uiStat);
      }
    });

    return {
      match_id: matchId,
      home_team_stats: homeTeamStats,
      away_team_stats: awayTeamStats
    };
  },

  /**
   * Get all players from match_lineup for a match
   * Returns players with composite key (player_id + team_id) to handle same player in multiple teams
   */
  async getMatchLineupPlayers(matchId: string): Promise<Array<{ player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string }>> {
    const { data, error } = await supabase
      .from('match_lineup')
      .select(`
        player_id,
        team_id,
        players!match_lineup_player_id_fkey(id, full_name, kit_number),
        teams!match_lineup_team_id_fkey(id, name)
      `)
      .eq('match_id', matchId);

    if (error) {
      console.error('Error fetching match lineup players:', error);
      throw error;
    }

    // Transform and deduplicate by composite key (player_id + team_id)
    // This allows the same player to appear in multiple teams
    const playersMap = new Map<string, { player_id: string; full_name: string; kit_number: number | null; team_id: string; team_name: string }>();
    
    (data || []).forEach((entry: any) => {
      if (entry.player_id && entry.team_id && entry.players) {
        // Use composite key: player_id + team_id
        const compositeKey = `${entry.player_id}-${entry.team_id}`;
        if (!playersMap.has(compositeKey)) {
          playersMap.set(compositeKey, {
            player_id: entry.player_id,
            full_name: entry.players.full_name || 'Unknown Player',
            kit_number: entry.players.kit_number || null,
            team_id: entry.team_id,
            team_name: entry.teams?.name || 'Unknown Team'
          });
        }
      }
    });

    // Sort by team name, then player name
    return Array.from(playersMap.values()).sort((a, b) => {
      if (a.team_name !== b.team_name) {
        return a.team_name.localeCompare(b.team_name);
      }
      return a.full_name.localeCompare(b.full_name);
    });
  },

  /**
   * Get player stats for a match - for reports (only visible stats)
   * Requires team_id to properly filter stats for player in specific team context
   */
  async getPlayerStatsForReports(matchId: string, playerId: string, teamId: string): Promise<UIMatchStats> {
    const { data: playerStatsData, error } = await supabase
      .from('player_scoresheet')
      .select(`
        *,
        stat_type:stat_types(*),
        player:players(id, full_name),
        team:teams(id, name)
      `)
      .eq('match_id', matchId)
      .eq('player_id', playerId)
      .eq('team_id', teamId); // Filter by team_id to get stats for specific team

    if (error) {
      console.error('Error fetching player stats for reports:', error);
      throw error;
    }

    // Filter to only include stats marked as visible in reports
    const filteredData = playerStatsData?.filter(entry => 
      entry.stat_type && entry.stat_type.visible_in_reports === true
    ) || [];

    // Get match details to identify home and away teams
    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .select('home_team_id, away_team_id')
      .eq('id', matchId)
      .single();

    if (matchError) {
      console.error('Error fetching match details:', matchError);
      throw matchError;
    }

    const homeTeamId = matchData.home_team_id;
    const awayTeamId = matchData.away_team_id;

    // Transform to UIScoresheetStat format
    const playerStats: UIScoresheetStat[] = filteredData.map(entry => ({
      stat_type: entry.stat_type,
      value: entry.value,
      team_id: entry.team_id
    }));

    return {
      match_id: matchId,
      home_team_stats: teamId === homeTeamId ? playerStats : [],
      away_team_stats: teamId === awayTeamId ? playerStats : []
    };
  },

  /**
   * Get all player stats for a match - for score app (all stats)
   * Requires team_id to properly filter stats for player in specific team context
   */
  async getPlayerStats(matchId: string, playerId: string, teamId: string): Promise<UIMatchStats> {
    const { data: playerStatsData, error } = await supabase
      .from('player_scoresheet')
      .select(`
        *,
        stat_type:stat_types(*),
        player:players(id, full_name),
        team:teams(id, name)
      `)
      .eq('match_id', matchId)
      .eq('player_id', playerId)
      .eq('team_id', teamId); // Filter by team_id to get stats for specific team

    if (error) {
      console.error('Error fetching player stats:', error);
      throw error;
    }

    // Transform to UIScoresheetStat format
    const playerStats: UIScoresheetStat[] = (playerStatsData || []).map(entry => ({
      stat_type: entry.stat_type,
      value: entry.value,
      team_id: entry.team_id
    }));

    // Get match details to identify home and away teams
    const { data: matchData, error: matchError } = await supabase
      .from('matches')
      .select('home_team_id, away_team_id')
      .eq('id', matchId)
      .single();

    if (matchError) {
      console.error('Error fetching match details:', matchError);
      throw matchError;
    }

    const homeTeamId = matchData.home_team_id;
    const awayTeamId = matchData.away_team_id;

    return {
      match_id: matchId,
      home_team_stats: teamId === homeTeamId ? playerStats : [],
      away_team_stats: teamId === awayTeamId ? playerStats : []
    };
  },
  async updateStat(
    matchId: string, 
    teamId: string, 
    statKey: string, 
    action: 'increment' | 'decrement',
    userId?: string,
    matchMinute?: number,
    playerId?: string,
    positionData?: any,
    videoTimeSeconds?: number | null,
    videoId?: string | null,
    manualTags?: string[],
    isLive?: boolean // Optional: if provided, use cached value instead of querying
  ): Promise<void> {
    // Update stat value
    
    // Check if match is live (skip validation for game_flow stats)
    if (!['kick_off', 'end_1st_half', 'start_2nd_half', 'full_time', 'substitution'].includes(statKey)) {
      // Use provided isLive value if available (from cached matchStatus), otherwise query
      let matchIsLive: boolean;
      if (isLive !== undefined) {
        matchIsLive = isLive;
      } else {
        // Fallback: query if isLive not provided (backward compatibility)
        const { MatchTimelineService } = await import('./matchTimelineService');
        matchIsLive = await MatchTimelineService.isMatchLive(matchId);
      }
      
      if (!matchIsLive) {
        throw new Error('Cannot update stats - match is not live');
      }
    }

    // For substitution stats, ensure the stat type exists
    if (statKey === 'substitution') {
      await this.ensureSubstitutionStatExists();
    }
    
    const statTypeId = await this.getStatTypeId(statKey);
    // Get stat type ID

    // Parallelize: Get current value and get timing points (if video provided)
    const [currentValueResult, timingPointsResult] = await Promise.allSettled([
      // Get current value
      supabase
        .from('scoresheet')
        .select('value')
        .eq('match_id', matchId)
        .eq('team_id', teamId)
        .eq('stat_type_id', statTypeId)
        .single(),
      
      // Get video's timing points (if video provided)
      (videoId && videoTimeSeconds !== null && videoTimeSeconds !== undefined)
        ? (async () => {
            try {
              const { matchVideosService } = await import('./matchVideosService');
              return await matchVideosService.getTimingPoints(videoId!);
            } catch (error) {
              console.warn('Error getting timing points:', error);
              return { success: false, data: null };
            }
          })()
        : Promise.resolve({ success: false, data: null })
    ]);

    // Handle current value result
    if (currentValueResult.status === 'rejected') {
      throw currentValueResult.reason;
    }
    const fetchError = currentValueResult.value.error;
    if (fetchError && fetchError.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error fetching current stat value:', fetchError);
      throw fetchError;
    }

    const currentValue = currentValueResult.value.data?.value || 0;
    const newValue = Math.max(0, currentValue + (action === 'increment' ? 1 : -1));

    // Calculate match minute from video timing if available
    let calculatedMatchMinute: number | null = null;
    let logVideoTimeSeconds: number | null = null;
    let logVideoId: string | null = null;

    if (timingPointsResult.status === 'fulfilled' && timingPointsResult.value.success && timingPointsResult.value.data) {
      try {
        const { calculateMatchMinute } = await import('../utils/videoTimingUtils');
        calculatedMatchMinute = calculateMatchMinute(videoTimeSeconds!, timingPointsResult.value.data);
        logVideoTimeSeconds = videoTimeSeconds!;
        logVideoId = videoId!;

        // If this is a break event, add timing point to video
        const breakEvents = ['start_2nd_half', 'start_extra_time_1st_half', 'start_extra_time_2nd_half'];
        if (breakEvents.includes(statKey) && calculatedMatchMinute !== null) {
          const { matchVideosService } = await import('./matchVideosService');
          await matchVideosService.addTimingPoint(
            videoId!,
            calculatedMatchMinute,
            videoTimeSeconds!,
            statKey,
            undefined // sourceLogId will be set after log is created
          );
        }
      } catch (error) {
        console.warn('Error calculating match minute from video timing:', error);
        // Continue without video timing - don't break the stat update
      }
    }

    // Use calculated match minute if available, otherwise use provided matchMinute, otherwise null
    const finalMatchMinute = calculatedMatchMinute !== null ? calculatedMatchMinute : (matchMinute ?? null);

    // Parallelize: Upsert scoresheet and log stat change (independent operations)
    const [upsertResult, logResult] = await Promise.allSettled([
      // Update or insert the stat
      supabase
        .from('scoresheet')
        .upsert({
          match_id: matchId,
          team_id: teamId,
          stat_type_id: statTypeId,
          value: newValue,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'match_id,team_id,stat_type_id'
        }),
      
      // Log the change
      this.logStatChange(
        matchId,
        teamId,
        statTypeId,
        action,
        currentValue,
        newValue,
        userId,
        finalMatchMinute,
        playerId,
        positionData,
        logVideoTimeSeconds,
        logVideoId,
        manualTags
      )
    ]);

    // Handle upsert result
    if (upsertResult.status === 'rejected') {
      throw upsertResult.reason;
    }
    if (upsertResult.value.error) {
      console.error('Error updating stat:', upsertResult.value.error);
      throw upsertResult.value.error;
    }

    // Handle log result (logStatChange returns Promise<string | null>)
    const logId = logResult.status === 'fulfilled' ? logResult.value : null;

    // If this was a break event and we have a log ID, update the timing point with source_log_id
    if (logId && videoId && videoTimeSeconds !== null && videoTimeSeconds !== undefined) {
      const breakEvents = ['start_2nd_half', 'start_extra_time_1st_half', 'start_extra_time_2nd_half'];
      if (breakEvents.includes(statKey) && calculatedMatchMinute !== null) {
        try {
          const { matchVideosService } = await import('./matchVideosService');
          await matchVideosService.addTimingPoint(
            videoId,
            calculatedMatchMinute,
            videoTimeSeconds, // TypeScript now knows this is number (not undefined) due to the check above
            statKey,
            logId
          );
        } catch (error) {
          console.warn('Error updating timing point with log ID:', error);
          // Non-critical - timing point was already added
        }
      }
    }

    console.log(`Successfully updated ${statKey}: ${currentValue} -> ${newValue}`);

    // Update match scores if this is a goal-related stat
    if (['shot_inside_box_goal', 'shot_outside_box_goal', 'penalty_scored', 'own_goal'].includes(statKey)) {
      try {
        const { calculatedStatsService } = await import('./calculatedStatsService');
        await calculatedStatsService.updateMatchScoresOnly(matchId);
        console.log(`✅ Match scores updated after ${statKey} change`);
        
        // Trigger a custom event to notify UI components to refresh match details
        window.dispatchEvent(new CustomEvent('matchScoresUpdated', { 
          detail: { matchId } 
        }));
      } catch (scoreError) {
        console.error('❌ Error updating match scores after stat change:', scoreError);
        // Don't throw here - score update failure shouldn't break the main stat update
      }
    }

    // Update penalty shootout scores in matches table if this is a penalty shootout stat
    if (['penalty_shootout_scored', 'penalty_shootout_missed'].includes(statKey)) {
      try {
        await this.updatePenaltyScoresInMatches(matchId);
        console.log(`✅ Penalty scores updated in matches table after ${statKey} change`);
        
        // Trigger a custom event to notify UI components to refresh penalty scores
        window.dispatchEvent(new CustomEvent('penaltyScoresUpdated', { 
          detail: { matchId } 
        }));
      } catch (penaltyError) {
        console.error('❌ Error updating penalty scores in matches table:', penaltyError);
        // Don't throw here - penalty score update failure shouldn't break the main stat update
      }
    }
  },

  /**
   * Batch update multiple stats
   */
  async batchUpdateStats(
    matchId: string,
    teamId: string,
    statUpdates: Array<{ statKey: string; action: 'increment' | 'decrement' }>,
    userId?: string
  ): Promise<void> {
    console.log(`Batch updating ${statUpdates.length} stats for team: ${teamId}`);
    
    // Process all updates in a transaction-like manner
    const promises = statUpdates.map(update => 
      this.updateStat(matchId, teamId, update.statKey, update.action, userId)
    );

    await Promise.all(promises);
    console.log('Batch update completed successfully');
  },

  /**
   * Log a stat change for audit trail
   * @returns The ID of the created log entry (for linking timing points)
   */
  async logStatChange(
    matchId: string,
    teamId: string,
    statTypeId: string,
    action: 'increment' | 'decrement',
    previousValue: number,
    newValue: number,
    userId?: string,
    matchMinute?: number | null,
    playerId?: string,
    positionData?: any,
    videoTimeSeconds?: number | null,
    videoId?: string | null,
    manualTags?: string[]
  ): Promise<string | null> {
    const { data, error } = await supabase
      .from('scoresheet_logs')
      .insert({
        match_id: matchId,
        team_id: teamId,
        stat_type_id: statTypeId,
        action,
        previous_value: previousValue,
        new_value: newValue,
        match_minute: matchMinute ?? null, // Use calculated or provided match minute
        user_id: userId,
        player_id: playerId || null,
        position_data: positionData || null,
        video_time_seconds: videoTimeSeconds ?? null,
        video_id: videoId ?? null,
        manual_tags: manualTags && manualTags.length > 0 ? manualTags : null,
        timestamp: new Date().toISOString(),
        is_active: true
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error logging stat change:', error);
      // Don't throw here - logging failure shouldn't break the main operation
      return null;
    }

    const logId = data?.id || null;

    // Real-time sync to match_video_tags (if video timing provided)
    if (logId && videoId && videoTimeSeconds !== null && videoTimeSeconds !== undefined) {
      try {
        const { VideoTagsSyncService } = await import('./videoTagsSyncService');
        await VideoTagsSyncService.syncVideoTagsForLog(matchId, logId, userId);
      } catch (error) {
        console.warn('Error syncing video tag for log:', error);
        // Non-critical - don't break the main operation
      }
    }

    return logId;
  },

  /**
   * Get stat change history for a match
   */
  async getStatHistory(matchId: string, statKey?: string): Promise<ScoresheetLog[]> {
    console.log(`Fetching stat history for match: ${matchId}${statKey ? `, stat: ${statKey}` : ''}`);
    
    let query = supabase
      .from('scoresheet_logs')
      .select(`
        *,
        stat_type:stat_types(*),
        user:users(email)
      `)
      .eq('match_id', matchId)
      .order('timestamp', { ascending: false });

    if (statKey) {
      const statTypeId = await this.getStatTypeId(statKey);
      query = query.eq('stat_type_id', statTypeId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching stat history:', error);
      throw error;
    }

    return data || [];
  },

  /**
   * Initialize scoresheet for a match (create zero entries for all stats)
   */
  async initializeMatchScoresheet(matchId: string, homeTeamId: string, awayTeamId: string): Promise<void> {
    console.log(`Initializing scoresheet for match: ${matchId}`);
    
    // Get all active stat types
    const statTypes = await this.getStatTypes();
    
    // Create zero entries for both teams
    const entries: Array<{
      match_id: string;
      team_id: string;
      stat_type_id: string;
      value: number;
    }> = [];
    
    statTypes.forEach(statType => {
      // Home team entry
      entries.push({
        match_id: matchId,
        team_id: homeTeamId,
        stat_type_id: statType.id,
        value: 0
      });
      
      // Away team entry
      entries.push({
        match_id: matchId,
        team_id: awayTeamId,
        stat_type_id: statType.id,
        value: 0
      });
    });

    const { error } = await supabase
      .from('scoresheet')
      .insert(entries);

    if (error) {
      console.error('Error initializing match scoresheet:', error);
      throw error;
    }

    console.log(`Initialized scoresheet with ${entries.length} entries`);
  },

  /**
   * Clear all stats for a match (reset to zero)
   */
  async clearMatchStats(matchId: string): Promise<void> {
    console.log(`Clearing all stats for match: ${matchId}`);
    
    const { error } = await supabase
      .from('scoresheet')
      .update({ value: 0, updated_at: new Date().toISOString() })
      .eq('match_id', matchId);

    if (error) {
      console.error('Error clearing match stats:', error);
      throw error;
    }

    console.log('Match stats cleared successfully');
  },

  /**
   * Update penalty shootout scores in matches table
   */
  async updatePenaltyScoresInMatches(matchId: string): Promise<void> {
    console.log(`Updating penalty scores in matches table for match: ${matchId}`);
    
    try {
      // Get match details to identify home and away teams
      const { data: match, error: matchError } = await supabase
        .from('matches')
        .select('home_team_id, away_team_id')
        .eq('id', matchId)
        .single();

      if (matchError) {
        console.error('Error fetching match details:', matchError);
        throw matchError;
      }

      // Get penalty shootout scores for both teams
      const { data: penaltyStats, error: statsError } = await supabase
        .from('scoresheet')
        .select(`
          team_id,
          value,
          stat_types!inner(stat_key)
        `)
        .eq('match_id', matchId)
        .in('stat_types.stat_key', ['penalty_shootout_scored', 'penalty_shootout_missed']);

      if (statsError) {
        console.error('Error fetching penalty stats:', statsError);
        throw statsError;
      }

      // Calculate penalty scores for each team
      let homePenaltyScore = 0;
      let awayPenaltyScore = 0;

      penaltyStats?.forEach(stat => {
        const statKey = (stat.stat_types as any)?.stat_key;
        const value = stat.value || 0;

        if (statKey === 'penalty_shootout_scored') {
          if (stat.team_id === match.home_team_id) {
            homePenaltyScore += value;
          } else if (stat.team_id === match.away_team_id) {
            awayPenaltyScore += value;
          }
        }
      });

      // Update matches table with penalty scores
      const { error: updateError } = await supabase
        .from('matches')
        .update({
          home_penalty_score: homePenaltyScore,
          away_penalty_score: awayPenaltyScore,
          updated_at: new Date().toISOString()
        })
        .eq('id', matchId);

      if (updateError) {
        console.error('Error updating penalty scores in matches table:', updateError);
        throw updateError;
      }

      console.log(`✅ Penalty scores updated: Home ${homePenaltyScore}, Away ${awayPenaltyScore}`);
    } catch (error) {
      console.error('Exception updating penalty scores in matches table:', error);
      throw error;
    }
  }
};
